import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import {
  convertUTCTimeToLocalTime,
  getTimeZoneAbbreviation,
} from "../../../utils/dateUtils";
import SectionCard from "./SectionCard";
import DetailRow from "./DetailRow";
import { detailRowStyles as styles } from "./styles";
import { formatDayLabel } from "./utils";

// Same conversion the editable drawer's schedule summary already uses
// (Environments/utils.js getScheduleSummary), so both drawers agree.
const TIME_ZONE_ABBR = getTimeZoneAbbreviation();

// Auto hibernate / auto wake up share the same "On/Off · <sentence>" shape,
// built from the raw config (utctime/repeat/notifyBeforeMinutes), with the
// time converted to the browser's local time for display. The day label is
// still derived from the raw UTC day-indices — a schedule whose local time
// falls the other side of midnight from its UTC time can, in principle, read
// against the "wrong" day; the editable drawer's schedule summary has this
// same limitation.
const ScheduleRow = ({
  label,
  enabled,
  prefix,
  utctime,
  repeat,
  notifyMinutes,
  extraLine,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const dayLabel = formatDayLabel(repeat);
  const localTime = utctime ? convertUTCTimeToLocalTime(utctime) : null;
  const sentence =
    dayLabel && localTime ? `${dayLabel} ${localTime} ${TIME_ZONE_ABBR}` : null;
  const notifyLine =
    Array.isArray(notifyMinutes) && notifyMinutes.length
      ? `notifies ${notifyMinutes.join(", ")} min before`
      : null;

  return (
    <Box sx={styles.row(colors)}>
      <Box sx={styles.label(colors)}>{label}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={styles.valueGroup}>
          <Typography
            component="span"
            sx={{
              fontSize: "12.5px",
              fontWeight: 600,
              color: enabled ? colors.green[400] : colors.grey[400],
            }}
          >
            {enabled ? "On" : "Off"}
          </Typography>
          {sentence && (
            <Typography component="span" sx={styles.value(colors)}>
              &nbsp;·&nbsp;{prefix ? `${prefix} ` : ""}
              {sentence}
            </Typography>
          )}
        </Box>
        {notifyLine && (
          <Typography sx={styles.subLine(colors)}>{notifyLine}</Typography>
        )}
        {extraLine && (
          <Typography sx={styles.subLine(colors)}>{extraLine}</Typography>
        )}
      </Box>
    </Box>
  );
};

const SchedulesCard = ({ row }) => {
  const hibernate = row?.config?.hibernate;
  const wakeup = row?.config?.wakeup;
  const cleanup = row?.config?.dbServicesAutoDelete;

  if (!hibernate && !wakeup && !cleanup) return null;

  return (
    <SectionCard title="Schedules">
      {hibernate && (
        <ScheduleRow
          label="Auto hibernate"
          enabled={Boolean(hibernate.enableAutomaticHibernation)}
          utctime={hibernate.schedule?.utctime}
          repeat={hibernate.schedule?.repeat}
          notifyMinutes={hibernate.schedule?.notifyBeforeMinutes}
        />
      )}
      {wakeup && (
        <ScheduleRow
          label="Auto wake up"
          enabled={Boolean(wakeup.enableAutomaticWakeup)}
          prefix={wakeup.enableAutomaticWakeup ? undefined : "would run"}
          utctime={wakeup.schedule?.utctime}
          repeat={wakeup.schedule?.repeat}
          notifyMinutes={wakeup.schedule?.notifyBeforeMinutes}
          extraLine={`refresh on wake up ${
            wakeup.refreshOnAutomaticWakeup ? "on" : "off"
          }${
            wakeup.refreshBranchName
              ? ` · branch ${wakeup.refreshBranchName}`
              : ""
          }`}
        />
      )}
      {cleanup && (
        <DetailRow
          label="DB service cleanup"
          value={
            typeof cleanup.deleteAfterHours === "number"
              ? `deletes after ${cleanup.deleteAfterHours} h`
              : null
          }
          subLine={`delete-protected services ${
            cleanup.skipDeleteProtected ? "excluded" : "included"
          }`}
        />
      )}
    </SectionCard>
  );
};

export default SchedulesCard;
